import { Clock } from "lucide-react";
import { useCreatePath, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";

import { ReplyClockBadge } from "../deals/ReplyClockBadge";
import { computeReplyClock, type ReplyClockState } from "../deals/replyClock";
import { usePublicHolidays } from "../deals/usePublicHolidays";
import type { Deal } from "../types";

const MAX_OPEN_CLOCK_ENQUIRIES = 1000;

type Listed = { deal: Deal; sortKey: number };

const DueListCard = ({ title, items }: { title: string; items: Listed[] }) => {
  const translate = useTranslate();
  const createPath = useCreatePath();
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-xl font-semibold text-muted-foreground">{title}</h2>
      <Card className="p-4 mb-2">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.dashboard.reply_due.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map(({ deal }) => (
              <li key={deal.id}>
                <Link
                  to={createPath({
                    resource: "deals",
                    id: deal.id,
                    type: "show",
                  })}
                  className="flex items-center gap-2 text-sm hover:underline"
                >
                  <span className="font-mono text-xs text-muted-foreground">
                    {deal.job_number}
                  </span>
                  <span className="flex-1 truncate">{deal.name}</span>
                  <ReplyClockBadge deal={deal} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

/**
 * Dashboard lists of enquiries whose first-reply clock is due soon or overdue.
 * Only open-clock enquiries (received, no first reply, not archived) are
 * fetched; won / lost ones are dropped by the clock itself.
 */
export const EnquiryReplyDueList = () => {
  const translate = useTranslate();
  const { holidays, isPending: isPendingHolidays } = usePublicHolidays();
  const { data, isPending } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: MAX_OPEN_CLOCK_ENQUIRIES },
    sort: { field: "received_at", order: "ASC" },
    filter: {
      "first_reply_at@is": null,
      "archived_at@is": null,
      "received_at@not.is": null,
    },
  });
  if (isPending || isPendingHolidays) return null;

  const now = new Date();
  const byState = (state: ReplyClockState) =>
    (data ?? [])
      .map((deal) => ({ deal, clock: computeReplyClock(deal, holidays, now) }))
      .filter(({ clock }) => clock.state === state)
      .map(({ deal, clock }) => ({ deal, sortKey: clock.hoursLeft ?? 0 }))
      .sort((a, b) => a.sortKey - b.sortKey);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center">
        <Clock className="text-muted-foreground w-6 h-6 mr-3" />
        <span className="sr-only">
          {translate("crm.dashboard.reply_due.title")}
        </span>
      </div>
      <DueListCard
        title={translate("crm.dashboard.reply_due.overdue")}
        items={byState("overdue")}
      />
      <DueListCard
        title={translate("crm.dashboard.reply_due.due_soon")}
        items={byState("due-soon")}
      />
    </div>
  );
};
